// frontend/src/pages/Admin/UserManagement.jsx - VERSIÓN FINAL

import React, { useState, useEffect } from 'react';
import { usersAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { ROLE_NAMES } from '../../utils/permissions';
import UserApprovalModal from '../../components/Modals/UserApprovalModal';
import UserPermissionsModal from '../../components/Modals/UserPermissionsModal';
import {
  Card,
  CardHeader,
  CardBody,
  Table,
  Badge,
  Button,
  Input,
  Row,
  Col,
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
} from 'reactstrap';
import { parseDate } from '../../utils/dates';

function UserManagement() {
  const { user: currentUser } = useAuth();
  const [pendingUsers, setPendingUsers] = useState([]);
  const [approvedUsers, setApprovedUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [showPermissionsModal, setShowPermissionsModal] = useState(false);
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      setLoading(true);
      const [pendingResponse, allResponse] = await Promise.all([
        usersAPI.getPending(),
        usersAPI.getAll(),
      ]);

      setPendingUsers(pendingResponse.data.users || []);
      setApprovedUsers(
        (allResponse.data.users || []).filter((u) => u.is_approved)
      );
    } catch (error) {
      console.error('Error cargando usuarios:', error);
      alert('Error al cargar usuarios');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = (user) => {
    setSelectedUser(user);
    setShowApprovalModal(true);
  };

  const handleReject = async (userId) => {
    if (!window.confirm('¿Estás seguro de rechazar este usuario?')) return;

    try {
      await usersAPI.reject(userId);
      alert('Usuario rechazado exitosamente');
      loadUsers();
    } catch (error) {
      console.error('Error rechazando usuario:', error);
      alert('Error al rechazar usuario');
    }
  };

  const handleEditPermissions = (user) => {
    setSelectedUser(user);
    setShowPermissionsModal(true);
  };

  const handleDelete = async (userId) => {
    if (!window.confirm('¿Estás seguro de eliminar este usuario? Esta acción no se puede deshacer.')) return;

    try {
      await usersAPI.delete(userId);
      alert('Usuario eliminado exitosamente');
      loadUsers();
    } catch (error) {
      console.error('Error eliminando usuario:', error);
      alert('Error al eliminar usuario');
    }
  };

  // LÍNEA 114-122 (REEMPLAZAR COMPLETO)
const filteredUsers = approvedUsers.filter((user) => {
  const matchesFilter = filter === 'all' || user.role === filter;
  const searchLower = searchTerm.toLowerCase();
  const matchesSearch =
    (user.full_name && user.full_name.toLowerCase().includes(searchLower)) ||
    (user.email && user.email.toLowerCase().includes(searchLower)) ||
    (user.phone && user.phone.toLowerCase().includes(searchLower)) ||
    (user.first_name && user.first_name.toLowerCase().includes(searchLower)) ||
    (user.last_name && user.last_name.toLowerCase().includes(searchLower));
  return matchesFilter && matchesSearch;
});

  if (loading) {
    return (
      <div className="container-fluid py-4">
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status" style={{ width: '3rem', height: '3rem' }}>
            <span className="visually-hidden">Cargando...</span>
          </div>
          <p className="text-muted mt-3">Cargando usuarios...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid py-4">
      {/* ✅ Header con estadísticas */}
      <Row className="mb-4">
        <Col lg="12">
          <Card className="shadow border-0">
            <CardBody className="p-3">
              <Row className="align-items-center">
                <Col lg="8">
                  <div className="d-flex align-items-center">
                    <div className="icon icon-shape bg-gradient-primary shadow text-center rounded-circle me-3">
                      <i className="ni ni-single-02 text-white text-lg opacity-10"></i>
                    </div>
                    <div>
                      <h5 className="mb-0">Gestión de Usuarios</h5>
                      <p className="text-sm mb-0 text-secondary">
                        Administra usuarios y permisos del sistema
                      </p>
                    </div>
                  </div>
                </Col>
                <Col lg="4" className="text-end">
                  <Badge color="warning" pill className="badge-lg me-2">
                    <i className="ni ni-time-alarm me-1"></i>
                    {pendingUsers.length} pendientes
                  </Badge>
                  <Badge color="success" pill className="badge-lg">
                    <i className="ni ni-check-bold me-1"></i>
                    {approvedUsers.length} aprobados
                  </Badge>
                </Col>
              </Row>
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* ✅ Usuarios Pendientes */}
      {pendingUsers.length > 0 && (
        <Row className="mb-4">
          <Col lg="12">
            <Card className="shadow border-0">
              <CardHeader className="bg-gradient-primary border-0">
                <h6 className="mb-0 text-white">
                  <i className="ni ni-time-alarm me-2"></i>
                  Usuarios Pendientes de Aprobación ({pendingUsers.length})
                </h6>
              </CardHeader>
              <CardBody className="px-0 pt-0 pb-2">
                <div className="table-responsive p-0">
                  <Table className="align-items-center mb-0">
                    <thead>
                      <tr>
                        <th className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                          Usuario
                        </th>
                        <th className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-2">
                          Email
                        </th>
                        <th className="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                          Fecha de Registro
                        </th>
                        <th className="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                          Acciones
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {pendingUsers.map((user) => (
                        <tr key={user.id}>
                          <td>
                            <div className="d-flex px-3 py-1">
                              {user.profile_picture ? (
                                <img
                                  src={user.profile_picture}
                                  className="avatar avatar-sm me-3 shadow"
                                  alt={user.full_name}
                                />
                              ) : (
                                <div className="avatar avatar-sm bg-gradient-primary me-3 shadow">
                                  <span className="text-white text-xs font-weight-bold">
                                    {user.first_name?.[0]}{user.last_name?.[0]}
                                  </span>
                                </div>
                              )}
                              <div className="d-flex flex-column justify-content-center">
                                <h6 className="mb-0 text-sm">{user.full_name}</h6>
                                {user.phone && (
                                  <p className="text-xs text-secondary mb-0">
                                    <i className="ni ni-mobile-button me-1"></i>
                                    {user.phone}
                                  </p>
                                )}
                              </div>
                            </div>
                          </td>
                          <td>
                            <p className="text-xs font-weight-bold mb-0">
                              <i className="ni ni-email-83 me-1"></i>
                              {user.email}
                            </p>
                          </td>
                          <td className="align-middle text-center">
                            <span className="text-secondary text-xs font-weight-bold">
                              {parseDate(user.created_at).toLocaleDateString('es-MX', {
                                year: 'numeric',
                                month: 'short',
                                day: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit',
                              })}
                            </span>
                          </td>
                          <td className="align-middle text-center">
                            <Button
                              onClick={() => handleApprove(user)}
                              color="success"
                              size="sm"
                              className="me-2 shadow-sm"
                            >
                              <i className="bg-gradient-primary"></i>
                              Aprobar
                            </Button>
                            <Button
                              onClick={() => handleReject(user.id)}
                              color="danger"
                              size="sm"
                              className="shadow-sm"
                            >
                              <i className="bg-gradient-primary"></i>
                              Rechazar
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>
      )}

      {/* ✅ Usuarios Aprobados */}
      <Row>
        <Col lg="12">
          <Card className="shadow border-0">
            <CardHeader className="bg-transparent border-0">
              <Row className="align-items-center">
                <Col lg="6">
                  <h6 className="mb-0">
                    <i className="ni ni-check-bold me-2 text-success"></i>
                    Usuarios Aprobados ({filteredUsers.length})
                  </h6>
                </Col>
                <Col lg="6">
                  <Row className="g-2">
                    {/* Filtro por rol */}
                    <Col md="6">
                      <select
                        className="form-select form-select-sm"
                        value={filter}
                        onChange={(e) => setFilter(e.target.value)}
                      >
                        <option value="all">
                          <i className="ni ni-bullet-list-67"></i>
                          Todos los roles
                        </option>
                        <option value="admin">Administrador</option>
                        <option value="sales">Ventas</option>
                        <option value="finance">Finanzas</option>
                        <option value="viewer">Visualizador</option>
                      </select>
                    </Col>

                    {/* Búsqueda */}
                    <Col md="6">
                      <Input
                        type="text"
                        className="form-control-sm"
                        placeholder="🔍 Buscar usuario..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                      />
                    </Col>
                  </Row>
                </Col>
              </Row>
            </CardHeader>
             <CardBody className="px-0 pt-0 pb-2">
              <div className="table-responsive p-0" style={{ overflow: 'visible' }}>
                <Table className="align-items-center mb-0">
                  <thead>
                    <tr>
                      <th className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                        Usuario
                      </th>
                      <th className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-2">
                        Email
                      </th>
                      <th className="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                        Rol
                      </th>
                      <th className="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                        Aprobado
                      </th>
                      <th className="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                        Acciones
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredUsers.length === 0 ? (
                      <tr>
                        <td colSpan="5" className="text-center py-4">
                          <div className="py-4">
                            <i className="ni ni-archive-2 text-muted" style={{ fontSize: '3rem' }}></i>
                            <p className="text-secondary mb-0 mt-2">
                              {searchTerm || filter !== 'all' 
                                ? 'No se encontraron usuarios con los filtros aplicados' 
                                : 'No hay usuarios aprobados'}
                            </p>
                          </div>
                        </td>
                      </tr>
                    ) : (
                      filteredUsers.map((user) => (
                        <tr key={user.id}>
                          <td>
                            <div className="d-flex px-3 py-1">
                              {user.profile_picture ? (
                                <img
                                  src={user.profile_picture}
                                  className="avatar avatar-sm me-3 shadow"
                                  alt={user.full_name}
                                />
                              ) : (
                                <div className="avatar avatar-sm bg-gradient-primary me-3 shadow">
                                  <span className="text-white text-xs font-weight-bold">
                                    {user.first_name?.[0]}{user.last_name?.[0]}
                                  </span>
                                </div>
                              )}
                              <div className="d-flex flex-column justify-content-center">
                                <h6 className="mb-0 text-sm">{user.full_name}</h6>
                                {user.phone && (
                                  <p className="text-xs text-secondary mb-0">
                                    <i className="ni ni-mobile-button me-1"></i>
                                    {user.phone}
                                  </p>
                                )}
                              </div>
                            </div>
                          </td>
                          <td>
                            <p className="text-xs font-weight-bold mb-0">
                              <i className="ni ni-email-83 me-1"></i>
                              {user.email}
                            </p>
                          </td>
                          <td className="align-middle text-center">
                            <Badge color="primary" className="badge-sm">
                              {ROLE_NAMES[user.role] || user.role}
                            </Badge>
                          </td>
                          <td className="align-middle text-center">
                            <div>
                              <span className="text-xs text-secondary">
                                <i className="ni ni-calendar-grid-58 me-1"></i>
                                {parseDate(user.approved_at).toLocaleDateString('es-MX')}
                              </span>
                              {user.approved_by_name && (
                                <p className="text-xxs text-secondary mb-0">
                                  por {user.approved_by_name}
                                </p>
                              )}
                            </div>
                          </td>
                          <td className="align-middle text-center" style={{ position: 'static' }}>
                            <UncontrolledDropdown>
                              <DropdownToggle
                                className="btn btn-sm btn-icon-only"
                                role="button"
                                size="sm"
                                color="secondary"
                                onClick={(e) => e.preventDefault()}
                                style={{ backgroundColor: '#6c757d', color: 'white' }}
                              >
                                <i className="fas fa-ellipsis-v" />
                              </DropdownToggle>
                              <DropdownMenu 
                                className="dropdown-menu-arrow" 
                                end
                                style={{ 
                                  position: 'fixed',
                                  willChange: 'transform'
                                }}
                                modifiers={[
                                  {
                                    name: 'preventOverflow',
                                    options: {
                                      boundary: 'viewport',
                                    },
                                  },
                                ]}
                              >
                                <DropdownItem
                                  onClick={() => handleEditPermissions(user)}
                                >
                                  <i className="ni ni-settings-gear-65 me-2 text-info"></i>
                                  Editar Permisos
                                </DropdownItem>
                                <DropdownItem divider />
                                <DropdownItem
                                  onClick={() => handleDelete(user.id)}
                                  className="text-danger"
                                >
                                  <i className="ni ni-fat-remove me-2"></i>
                                  Eliminar Usuario
                                </DropdownItem>
                              </DropdownMenu>
                            </UncontrolledDropdown>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </Table>
              </div>
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* ✅ Modales */}
      {showApprovalModal && (
        <UserApprovalModal
          user={selectedUser}
          onClose={() => {
            setShowApprovalModal(false);
            setSelectedUser(null);
          }}
          onSuccess={() => {
            setShowApprovalModal(false);
            setSelectedUser(null);
            loadUsers();
          }}
        />
      )}

      {showPermissionsModal && (
        <UserPermissionsModal
          user={selectedUser}
          onClose={() => {
            setShowPermissionsModal(false);
            setSelectedUser(null);
          }}
          onSuccess={() => {
            setShowPermissionsModal(false);
            setSelectedUser(null);
            loadUsers();
          }}
        />
      )}
    </div>
  );
}

export default UserManagement;
